import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // If user is already logged in, redirect to dashboard
  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleGetStarted = () => {
    navigate('/signup');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: FiIcons.FiEdit3,
      title: 'Create Interactive Stories',
      description: 'Build branching narratives where every choice matters. Craft compelling storylines with multiple paths and endings.'
    },
    {
      icon: FiIcons.FiUsers,
      title: 'Share with Community',
      description: 'Share your stories with friends or the entire community. Discover amazing tales created by other storytellers.'
    },
    {
      icon: FiIcons.FiImage,
      title: 'Visual Storytelling',
      description: 'Add images to bring your scenes to life. Create immersive experiences that captivate your readers.'
    },
    {
      icon: FiIcons.FiZap,
      title: 'Easy to Use',
      description: 'Intuitive visual editor makes story creation simple. No technical skills required - just your imagination!'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-mint-50">
      {/* Header */}
      <nav className="bg-white shadow-soft mb-6 py-3 px-4 rounded-xl mx-4 mt-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <motion.div
              whileHover={{ rotate: 10 }}
              className="bg-gradient-to-br from-primary-400 to-purple-600 text-white p-2 rounded-lg"
            >
              <SafeIcon icon={FiIcons.FiBook} className="text-xl" />
            </motion.div>
            <span className="text-xl font-display font-bold text-primary-600">
              Write Your Pathway
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium"
              onClick={handleSignIn}
            >
              Sign In
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl font-medium"
              onClick={handleGetStarted}
            >
              Get Started
            </motion.button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center py-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-primary-400 to-purple-600 rounded-3xl shadow-dreamy mb-6">
              <SafeIcon icon={FiIcons.FiBook} className="text-4xl text-white" />
            </div>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-5xl md:text-6xl font-display font-bold text-purple-700 mb-6"
          >
            Create Your Own
            <span className="block text-primary-500">Interactive Stories</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed"
          >
            Build branching narratives where every choice shapes the story. 
            Share your creations with the world and discover amazing tales from other storytellers.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-2xl font-bold text-lg flex items-center gap-3 shadow-dreamy"
              onClick={handleGetStarted}
            >
              <SafeIcon icon={FiIcons.FiPenTool} className="text-xl" />
              Start Creating Stories
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white hover:bg-gray-50 text-purple-700 px-8 py-4 rounded-2xl font-bold text-lg flex items-center gap-3 shadow-soft border border-purple-200"
              onClick={handleSignIn}
            >
              <SafeIcon icon={FiIcons.FiLogIn} className="text-xl" />
              I Have an Account
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Features Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="py-16"
        >
          <h2 className="text-3xl font-display font-bold text-center text-purple-700 mb-12">
            Everything You Need to Tell Your Story
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-3xl p-6 shadow-soft hover:shadow-dreamy transition-all duration-300"
              >
                <div className="bg-gradient-to-br from-primary-100 to-purple-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="text-2xl text-primary-600" />
                </div>
                <h3 className="text-xl font-display font-bold text-purple-700 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* How It Works Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="py-16 bg-white rounded-3xl shadow-soft mb-16"
        >
          <div className="px-8">
            <h2 className="text-3xl font-display font-bold text-center text-purple-700 mb-12">
              How It Works
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-gradient-to-br from-green-100 to-mint-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">1</span>
                </div>
                <h3 className="text-xl font-display font-bold text-purple-700 mb-3">
                  Create Your Story
                </h3>
                <p className="text-gray-600">
                  Write compelling content and add choices that branch your narrative in different directions.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">2</span>
                </div>
                <h3 className="text-xl font-display font-bold text-purple-700 mb-3">
                  Add Visual Elements
                </h3>
                <p className="text-gray-600">
                  Upload images to make your scenes come alive and create an immersive reading experience.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-gradient-to-br from-pink-100 to-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-pink-600">3</span>
                </div>
                <h3 className="text-xl font-display font-bold text-purple-700 mb-3">
                  Share & Discover
                </h3>
                <p className="text-gray-600">
                  Publish your story for others to enjoy and explore amazing interactive tales from the community.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="text-center py-16"
        >
          <h2 className="text-4xl font-display font-bold text-purple-700 mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of storytellers creating amazing interactive experiences.
            Your next great story is just a click away!
          </p>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-primary-500 to-purple-600 hover:from-primary-600 hover:to-purple-700 text-white px-10 py-5 rounded-2xl font-bold text-xl flex items-center gap-3 mx-auto shadow-dreamy"
            onClick={handleGetStarted}
          >
            <SafeIcon icon={FiIcons.FiStar} className="text-2xl" />
            Create Your First Story
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default LandingPage;